import React, { useEffect, useState, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import './Layout.css';

const NAV = [
  { to:'/',              icon:'⊞',  label:'Dashboard',     roles:['super','admin','employee'], end:true },
  { to:'/customers',    icon:'◎',   label:'Customers',     roles:['super','admin','employee'] },
  { to:'/bookings',     icon:'≡',   label:'Bookings',      roles:['super','admin','employee'] },
  { to:'/notifications',icon:'◈',  label:'Rebook Alerts', roles:['super','admin','employee'], alert:true },
  { to:'/reports',      icon:'◉',   label:'Reports',       roles:['super','admin'] },
  { to:'/users',        icon:'⊕',   label:'Users & Roles', roles:['super'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rebookCount, setRebookCount] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.getRebookEligible().then(r => setRebookCount(r.data.length)).catch(()=>{});
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const items = NAV.filter(n => n.roles.includes(user?.role));
  const current = items.find(n => n.end ? location.pathname==='/' : location.pathname.startsWith(n.to));
  const roleColor = { super:'var(--brand)', admin:'var(--accent-blue)', employee:'var(--accent-green)' }[user?.role];
  const roleLabel = { super:'Super Admin', admin:'Administrator', employee:'Employee' }[user?.role];

  return (
    <div className={`layout ${collapsed ? 'collapsed' : ''}`}>
      {/* Mobile overlay */}
      <div className={`sidebar-overlay ${mobileOpen ? 'active' : ''}`} onClick={() => setMobileOpen(false)} />

      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="logo-mark"><span>🔥</span></div>
          {!collapsed && (
            <div className="logo-text">
              <div className="logo-name">Venad Comms</div>
              <div className="logo-sub">IndianOil Gas Management</div>
            </div>
          )}
          <button className="collapse-btn" onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Expand' : 'Collapse'}>
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        {/* User */}
        {!collapsed && (
          <div className="sidebar-user">
            <div className="user-avatar" style={{ background:`${roleColor}18`, border:`1.5px solid ${roleColor}40`, color:roleColor }}>
              {user?.name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <div className="user-info">
              <div className="user-name truncate">{user?.name}</div>
              <div className="user-role" style={{color:roleColor}}>{roleLabel}</div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="sidebar-nav">
          {!collapsed && <div className="nav-section-label">Navigation</div>}
          {items.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `nav-item${isActive?' active':''}`}
              title={collapsed ? item.label : undefined}>
              <span className="nav-icon">{item.icon}</span>
              {!collapsed && <span className="nav-label">{item.label}</span>}
              {!collapsed && item.alert && rebookCount > 0 && <span className="nav-badge">{rebookCount}</span>}
              {collapsed && item.alert && rebookCount > 0 && <span className="nav-dot" />}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {!collapsed && <div className="sidebar-version"><span style={{color:'var(--text-muted)',fontSize:11,fontWeight:500}}>IndiaOil v2.1</span></div>}
          <button className="logout-btn" onClick={()=>{logout();navigate('/login');}} title="Sign Out">
            <span style={{fontSize:14}}>⏻</span>
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      <div className="main-wrap">
        {/* Topbar */}
        <header className="topbar">
          <div className="topbar-left">
            <button className="mobile-menu-btn" onClick={() => setMobileOpen(m => !m)}>☰</button>
            <h2 className="page-title-topbar">{current?.label || 'Dashboard'}</h2>
          </div>
          <div className="topbar-right">
            {rebookCount > 0 && (
              <button className="topbar-alert" onClick={() => navigate('/notifications')}>
                <span>🔔</span>
                <span>{rebookCount} alert{rebookCount>1?'s':''}</span>
                <span className="pulse-dot" />
              </button>
            )}
            <div className="topbar-date">
              {new Date().toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short',year:'numeric'})}
            </div>
            <div className="topbar-avatar" style={{ background:`${roleColor}18`, color:roleColor, borderColor:`${roleColor}30` }}>
              {user?.name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
            </div>
          </div>
        </header>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
